import {
  VIDEO_ALIGN,
  VIDEO_PROVIDERS,
  type VideoAlign,
  type VideoProvider,
  embeddedVideoEmbedUrl,
  embeddedVideoMarkdownLabel,
  normalizeVideoWidth,
  parseEmbeddedVideoUrl,
} from "@v1/backend/logic/pages/loom_video";
import {
  YOUTUBE_PROVIDER,
  youtubeEmbedUrl,
} from "@v1/backend/logic/pages/youtube_video";
import { cn } from "@v1/ui/utils";
import { useTranslations } from "next-intl";
import type { CSSProperties, ReactElement, Ref } from "react";

type EmbedIframeProvider = VideoProvider | typeof YOUTUBE_PROVIDER;

type EmbeddedVideoEmbedProps = {
  readonly provider: EmbedIframeProvider;
  readonly videoId: string;
  readonly widthPercent: number;
  readonly embedQuery: string;
  readonly align: VideoAlign;
  readonly className: string | undefined;
  readonly iframeRef?: Ref<HTMLIFrameElement>;
  /** Knowledge-page embeds stay lazy; the lesson player opts into eager. */
  readonly loading?: "lazy" | "eager";
  /** Optional YouTube `origin` query; omit on SSR so the iframe src stays stable. */
  readonly pageOrigin?: string;
};

/** Block margin that positions a width-constrained video left/center/right. */
function videoAlignClass(align: VideoAlign): string {
  if (align === VIDEO_ALIGN.CENTER) return "mx-auto";
  if (align === VIDEO_ALIGN.RIGHT) return "ml-auto";
  return "mr-auto";
}

/** The link text is intentionally absent: the embed replaces it entirely. */
type EmbeddedVideoMarkdownEmbedProps = {
  readonly href: string | undefined;
  readonly className: string | undefined;
};

/** Renders canonical video links emitted by the knowledge-page editor. */
export function renderEmbeddedVideoMarkdownEmbed({
  href,
  className,
}: EmbeddedVideoMarkdownEmbedProps): ReactElement | null {
  const video = parseEmbeddedVideoUrl(href ?? "");
  if (!video) return null;
  return (
    <EmbeddedVideoEmbed
      provider={video.provider}
      videoId={video.video_id}
      widthPercent={video.width_percent}
      embedQuery={video.embed_query}
      align={video.align}
      className={className}
    />
  );
}

/** Responsive video player used outside the editable ProseMirror node view. */
export function EmbeddedVideoEmbed({
  provider,
  videoId,
  widthPercent,
  embedQuery,
  align,
  className,
  iframeRef,
  loading = "lazy",
  pageOrigin,
}: EmbeddedVideoEmbedProps): ReactElement {
  const t = useTranslations("pages.loomVideoEmbed");
  const width = normalizeVideoWidth(widthPercent);
  const style: CSSProperties = { width: `${width}%` };
  const src: string =
    provider === YOUTUBE_PROVIDER
      ? youtubeEmbedUrl(videoId, embedQuery, pageOrigin)
      : embeddedVideoEmbedUrl(provider, videoId, embedQuery);
  const title: string =
    provider === YOUTUBE_PROVIDER
      ? embeddedVideoMarkdownLabel(VIDEO_PROVIDERS.YOUTUBE)
      : embeddedVideoMarkdownLabel(provider);
  return (
    <span
      data-embedded-video={provider}
      className={cn(
        "not-prose my-5 block w-full min-w-0 max-w-full",
        videoAlignClass(align),
        className,
      )}
      style={style}
    >
      <span className="relative block aspect-video overflow-hidden rounded-lg border border-border/60 bg-black shadow-sm">
        <iframe
          ref={iframeRef}
          id={provider === YOUTUBE_PROVIDER ? `yt-${videoId}` : undefined}
          name={provider === YOUTUBE_PROVIDER ? `yt-${videoId}` : undefined}
          src={src}
          title={title}
          // i18n-exempt: iframe-allow-Attribut, Maschinenwert
          allow="autoplay; fullscreen; picture-in-picture; encrypted-media"
          loading={loading}
          width="100%"
          height="100%"
          className="absolute inset-0 h-full w-full min-w-0 border-0"
        />
      </span>
    </span>
  );
}
